/**
 * Grafico de barras minimo para la venta diaria del panel.
 *
 * Las barras son decorativas: la lectura real esta en la tabla accesible que va
 * al lado, oculta a la vista pero disponible para lector de pantalla. Un
 * grafico que solo existe como dibujo deja fuera a quien no lo ve. El eje y el
 * globo con el monto al pasar el mouse son ayudas para quien lo mira.
 */
import { escalaGrafico } from '../../utilidades/escala-grafico';
import { fecha, pesos, pesosAbreviados } from '../../utilidades/formato';

export interface GraficoBarrasProps {
  /** Valores del mas antiguo al mas reciente. */
  valores: number[];
  etiqueta: string;
}

function fechaDeHaceDias(dias: number): string {
  const dia = new Date();
  dia.setDate(dia.getDate() - dias);
  return fecha(dia.toISOString());
}

function porcentaje(valor: number, tope: number): string {
  return `${(valor / tope) * 100}%`;
}

export function GraficoBarras({ valores, etiqueta }: GraficoBarrasProps) {
  const { tope, marcas } = escalaGrafico(valores);
  const dias = valores.map((_, indice) => fechaDeHaceDias(valores.length - 1 - indice));

  return (
    <figure className="grafico-barras">
      <div className="grafico-area" aria-hidden="true">
        <div className="grafico-eje">
          {marcas.map((marca) => (
            <span key={marca}>{pesosAbreviados(marca)}</span>
          ))}
        </div>

        <div className="grafico-lienzo">
          {marcas.map((marca) => (
            <span key={marca} className="grafico-guia" style={{ bottom: porcentaje(marca, tope) }} />
          ))}

          <div className="grafico-columnas">
            {valores.map((valor, indice) => (
              <span
                key={indice}
                // El globo se abre hacia adentro del grafico para no cortarse
                // contra el borde del panel.
                className={indice < valores.length / 2 ? 'grafico-col izq' : 'grafico-col der'}
              >
                <span className="grafico-barra" style={{ height: porcentaje(valor, tope) }} />
                <span className="grafico-globo" style={{ bottom: `calc(${porcentaje(valor, tope)} + 6px)` }}>
                  <span>{dias[indice]}</span>
                  <strong>{pesos(valor)}</strong>
                </span>
              </span>
            ))}
          </div>
        </div>

        <div className="grafico-pie">
          <span>hace {valores.length} días</span>
          <span>hoy</span>
        </div>
      </div>

      <table className="solo-lectores">
        <caption>{etiqueta}</caption>
        <thead>
          <tr>
            <th scope="col">Día</th>
            <th scope="col">Venta</th>
          </tr>
        </thead>
        <tbody>
          {valores.map((valor, indice) => (
            <tr key={indice}>
              <th scope="row">{dias[indice]}</th>
              <td>{pesos(valor)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
